import { create } from "zustand";
import { persist } from "zustand/middleware";
import { LS_CHAT_ASK_STORAGE } from "../shared/local-storage/keys";

export type ChatAskMessage = {
  role: "user" | "assistant";
  content: string;
  id: string;
};

type StreamState = {
  isStreaming: boolean;
  currentContent: string;
  statusMessage: string | null;
  phase: "idle" | "streaming" | "done";
};

type ChatAskHistory = {
  [projectId: string]: ChatAskMessage[];
};

type StreamStates = {
  [projectId: string]: StreamState;
};

interface ChatAskState {
  chatHistory: ChatAskHistory;
  streamStates: StreamStates;

  getChatHistory: (projectId: string) => ChatAskMessage[];
  addMessage: (projectId: string, message: ChatAskMessage) => void;
  clearHistory: (projectId: string) => void;

  getStreamState: (projectId: string) => StreamState;
  setStreamState: (projectId: string, updates: Partial<StreamState>) => void;
  appendContent: (projectId: string, content: string) => void;
  setStatusMessage: (projectId: string, message: string | null) => void;
  resetStreamState: (projectId: string) => void;
}

const createDefaultStreamState = (): StreamState => ({
  isStreaming: false,
  currentContent: "",
  statusMessage: null,
  phase: "idle"
});

export const useChatAskStore = create<ChatAskState>()(
  persist(
    (set, get) => ({
      chatHistory: {},
      streamStates: {},

      getChatHistory: (projectId: string) => {
        return get().chatHistory[projectId] || [];
      },

      addMessage: (projectId: string, message: ChatAskMessage) => {
        set((state) => {
          const messages = state.chatHistory[projectId] || [];
          if (messages.some((m) => m.id === message.id)) return state;
          return {
            chatHistory: {
              ...state.chatHistory,
              [projectId]: [...messages, message]
            }
          };
        });
      },

      clearHistory: (projectId: string) => {
        set((state) => ({
          chatHistory: {
            ...state.chatHistory,
            [projectId]: []
          }
        }));
      },

      getStreamState: (projectId: string) => {
        return get().streamStates[projectId] || createDefaultStreamState();
      },

      setStreamState: (projectId: string, updates: Partial<StreamState>) => {
        set((state) => {
          const current = state.streamStates[projectId] || createDefaultStreamState();
          return {
            streamStates: {
              ...state.streamStates,
              [projectId]: { ...current, ...updates }
            }
          };
        });
      },

      appendContent: (projectId: string, content: string) => {
        set((state) => {
          const current = state.streamStates[projectId] || createDefaultStreamState();
          return {
            streamStates: {
              ...state.streamStates,
              [projectId]: { ...current, currentContent: current.currentContent + content }
            }
          };
        });
      },

      setStatusMessage: (projectId: string, message: string | null) => {
        set((state) => {
          const current = state.streamStates[projectId] || createDefaultStreamState();
          return {
            streamStates: {
              ...state.streamStates,
              [projectId]: { ...current, statusMessage: message }
            }
          };
        });
      },

      resetStreamState: (projectId: string) => {
        set((state) => ({
          streamStates: {
            ...state.streamStates,
            [projectId]: createDefaultStreamState()
          }
        }));
      }
    }),
    {
      name: LS_CHAT_ASK_STORAGE,
      skipHydration: true,
      partialize: (state) => ({
        chatHistory: state.chatHistory
      })
    }
  )
);
